import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PortalLayout } from "../../components/portal/PortalLayout";
import { useBasket } from "../../context/BasketContext";
import { toast } from "sonner";
import { Search, Plus, Minus, ShoppingCart } from "lucide-react";

const STORAGE = ["Ambient", "Frozen", "Chilled"];

export default function PortalCatalogue() {
  const { add } = useBasket();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [storage, setStorage] = useState("");
  const [sort, setSort] = useState("name");
  const [qtys, setQtys] = useState({});
  useEffect(() => { document.title = "Trade Catalogue | Sambiva"; }, []);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["catalogue", category, storage, search, sort],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (category) q.set("category", category);
      if (storage) q.set("storage", storage);
      if (search) q.set("search", search);
      q.set("sort", sort);
      return (await api.get(`/trade/catalogue?${q.toString()}`)).data;
    },
  });

  const getQty = (id) => qtys[id] || 1;
  const setQty = (id, v) => setQtys((p) => ({ ...p, [id]: Math.max(1, v) }));

  const stockLabel = (p) => {
    if (p.stock_quantity <= 0) return <span className="text-sambiva-red font-medium">Out of stock</span>;
    if (p.stock_quantity <= p.low_stock_threshold) return <span className="text-[#8a6d00] font-medium">Low stock ({p.stock_quantity})</span>;
    return <span className="text-sambiva-green font-medium">In stock ({p.stock_quantity})</span>;
  };

  return (
    <PortalLayout title="Trade Catalogue">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sambiva-muted" size={18} />
          <input data-testid="catalogue-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products or SKU…" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-sambiva-charcoal/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sambiva-red/40" />
        </div>
        <select data-testid="catalogue-category" value={category} onChange={(e) => setCategory(e.target.value)} className="px-4 py-2.5 rounded-xl border border-sambiva-charcoal/15 bg-white text-sm">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select data-testid="catalogue-storage" value={storage} onChange={(e) => setStorage(e.target.value)} className="px-4 py-2.5 rounded-xl border border-sambiva-charcoal/15 bg-white text-sm">
          <option value="">All Storage</option>
          {STORAGE.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="catalogue-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="px-4 py-2.5 rounded-xl border border-sambiva-charcoal/15 bg-white text-sm">
          <option value="name">Name</option>
          <option value="newest">Newest</option>
          <option value="price">Price</option>
          <option value="availability">Availability</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-sambiva-muted">Loading…</div>
      ) : (
        <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-sambiva-cream text-sambiva-muted text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Product</th>
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Case</th>
                <th className="px-4 py-3 font-semibold">Trade Price</th>
                <th className="px-4 py-3 font-semibold">Stock</th>
                <th className="px-4 py-3 font-semibold">Qty</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`catalogue-row-${p.sku}`} className="border-t border-sambiva-charcoal/5 hover:bg-sambiva-cream/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.images?.[0]} alt="" className="w-11 h-11 rounded-lg object-cover" />
                      <span className="font-medium text-sambiva-charcoal">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sambiva-muted">{p.sku}</td>
                  <td className="px-4 py-3 text-sambiva-muted">{p.category}</td>
                  <td className="px-4 py-3 text-sambiva-charcoal">{p.case_quantity}</td>
                  <td className="px-4 py-3 font-semibold text-sambiva-green">€{p.trade_price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs">{stockLabel(p)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center border border-sambiva-charcoal/15 rounded-lg w-fit">
                      <button onClick={() => setQty(p.id, getQty(p.id) - 1)} className="p-1.5"><Minus size={14} /></button>
                      <span className="w-8 text-center" data-testid={`qty-${p.sku}`}>{getQty(p.id)}</span>
                      <button onClick={() => setQty(p.id, getQty(p.id) + 1)} className="p-1.5"><Plus size={14} /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`add-${p.sku}`}
                      disabled={p.stock_quantity <= 0}
                      onClick={() => { add(p, getQty(p.id)); toast.success("Product added to order."); }}
                      className="flex items-center gap-1.5 bg-sambiva-red hover:bg-sambiva-deepred text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 transition-colors"
                    >
                      <ShoppingCart size={14} /> Add
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <div className="p-10 text-center text-sambiva-muted">No products found. Try changing your filters.</div>}
        </div>
      )}
    </PortalLayout>
  );
}
